import Vue from 'vue'
import VueRouter from 'vue-router'
import Layout from '@/layout/index.vue'
import store from '@/store'
import { syncToolPageProtection, isToolRoute } from '@/utils/tool-page-guard'
import { isLikelyLoggedIn, clearAuthLocal } from '@/utils/auth'

Vue.use(VueRouter)

const AUTH_WHITE_LIST = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/v',
  '/qr.php',
  '/complaint',
  '/account-banned'
]

function isPublicRoute(path) {
  if (AUTH_WHITE_LIST.indexOf(path) !== -1) return true
  // /v/:uid 短链
  if (path === '/v' || path.indexOf('/v/') === 0) return true
  if (path === '/complaint' || path.indexOf('/complaint/') === 0) return true
  return false
}

function isBannedUser(user) {
  return !!(user && user.banned)
}

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { title: '登录' }
  },
  {
    path: '/v/:uid?',
    name: 'QrPublicView',
    component: () => import('@/views/qrcode/public-view.vue'),
    meta: { title: '扫码查看', public: true, hideCustomerService: true }
  },
  {
    path: '/complaint',
    name: 'QrComplaint',
    component: () => import('@/views/qrcode/complaint.vue'),
    meta: { title: '内容投诉', public: true, hideCustomerService: true }
  },
  {
    // 兼容旧二维码：/qr.php?url=xxx → 公开页
    path: '/qr.php',
    name: 'QrLegacyRedirect',
    meta: { public: true, hideCustomerService: true },
    redirect: (to) => ({
      path: '/v',
      query: { url: (to.query && (to.query.url || to.query.uid)) || '' }
    })
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/register/index.vue'),
    meta: { title: '注册' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/forgot-password/index.vue'),
    meta: { title: '找回密码' }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/reset-password/index.vue'),
    meta: { title: '重置密码' }
  },
  {
    path: '/account-banned',
    name: 'AccountBanned',
    component: () => import('@/views/account-banned/index.vue'),
    meta: { title: '账号受限', allowBanned: true }
  },
  {
    path: '/',
    component: Layout,
    redirect: '/home',
    children: [
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/views/home/index.vue'),
        meta: { title: '首页' }
      }
    ]
  },
  {
    path: '/qrcode',
    component: Layout,
    redirect: '/qrcode/generate',
    meta: { title: '二维码管理' },
    children: [
      {
        path: 'generate',
        name: 'QRCodeGenerate',
        component: () => import('@/views/qrcode/generate.vue'),
        meta: { title: '生成二维码' }
      },
      {
        path: 'list',
        name: 'QRCodeList',
        component: () => import('@/views/qrcode/list.vue'),
        meta: { title: '二维码列表' }
      },
      {
        path: 'image-space',
        name: 'ImageSpace',
        component: () => import('@/views/qrcode/image-space.vue'),
        meta: { title: '图片空间' }
      },
      {
        path: 'audio-space',
        name: 'AudioSpace',
        component: () => import('@/views/qrcode/audio-space.vue'),
        meta: { title: '音频空间' }
      },
      {
        path: 'document-space',
        name: 'DocumentSpace',
        component: () => import('@/views/qrcode/document-space.vue'),
        meta: { title: '文档空间' }
      }
    ]
  },
  {
    path: '/member',
    component: Layout,
    redirect: '/member/upgrade',
    meta: { title: '会员信息' },
    children: [
      {
        path: 'upgrade',
        name: 'MemberUpgrade',
        component: () => import('@/views/member/upgrade.vue'),
        meta: { title: '升级会员' }
      },
      {
        path: 'upgrade-diff',
        name: 'MemberUpgradeDiff',
        component: () => import('@/views/member/upgrade-diff.vue'),
        meta: { title: '补差价升级' }
      },
      {
        path: 'purchased',
        name: 'Purchased',
        component: () => import('@/views/member/purchased.vue'),
        meta: { title: '已购项目' }
      },
      {
        path: 'invoice',
        name: 'Invoice',
        component: () => import('@/views/member/invoice.vue'),
        meta: { title: '发票记录' }
      },
      {
        path: 'usage',
        name: 'Usage',
        component: () => import('@/views/member/usage.vue'),
        meta: { title: '会员及空间用量' }
      }
    ]
  },
  {
    path: '/tools',
    component: Layout,
    redirect: '/tools/image-compress',
    meta: { title: '在线工具', toolPage: true },
    children: [
      {
        path: 'image-compress',
        name: 'ImageCompress',
        component: () => import('@/views/tools/image-compress.vue'),
        meta: { title: '批量图片压缩', toolPage: true }
      },
      {
        path: 'image-convert',
        name: 'ImageConvert',
        component: () => import('@/views/tools/image-convert.vue'),
        meta: { title: '图片格式转换', toolPage: true }
      },
      {
        path: 'image-watermark',
        name: 'ImageWatermark',
        component: () => import('@/views/tools/image-watermark.vue'),
        meta: { title: '图片加水印', toolPage: true }
      },
      {
        path: 'image-crop',
        name: 'ImageCrop',
        component: () => import('@/views/tools/image-crop.vue'),
        meta: { title: '图片裁剪', toolPage: true }
      },
      {
        path: 'pdf-toolbox',
        name: 'PdfToolbox',
        component: () => import('@/views/tools/pdf-toolbox.vue'),
        meta: { title: 'PDF工具箱', toolPage: true }
      },
      {
        path: 'qrcode-decode',
        name: 'QrcodeDecode',
        component: () => import('@/views/tools/qrcode-decode.vue'),
        meta: { title: '二维码解码', toolPage: true }
      },
      {
        path: 'dpi-modify',
        name: 'DpiModify',
        component: () => import('@/views/tools/dpi-modify.vue'),
        meta: { title: '图片DPI修改', toolPage: true }
      },
      {
        path: 'video-convert',
        name: 'VideoConvert',
        component: () => import('@/views/tools/video-convert.vue'),
        meta: { title: '视频转码', toolPage: true }
      },
      {
        path: 'exif-editor',
        name: 'ExifEditor',
        component: () => import('@/views/tools/exif-editor.vue'),
        meta: { title: 'EXIF编辑', toolPage: true }
      },
      {
        path: 'image-overlay',
        name: 'ImageOverlay',
        component: () => import('@/views/tools/image-overlay.vue'),
        meta: { title: '图片叠加', toolPage: true }
      }
    ]
  },
  {
    path: '/user',
    component: Layout,
    redirect: '/user/settings',
    meta: { title: '用户资料' },
    children: [
      {
        path: 'settings',
        name: 'UserSettings',
        component: () => import('@/views/user/settings.vue'),
        meta: { title: '用户设置' }
      },
      {
        path: 'wechat',
        name: 'WeChatBind',
        component: () => import('@/views/user/wechat.vue'),
        meta: { title: '绑定微信' }
      }
    ]
  },
  {
    path: '/message',
    component: Layout,
    redirect: '/message',
    meta: { title: '站内信' },
    children: [
      {
        path: '',
        name: 'Message',
        component: () => import('@/views/message/index.vue'),
        meta: { title: '站内信' }
      }
    ]
  },
  {
    path: '/ticket',
    component: Layout,
    redirect: '/ticket',
    meta: { title: '我的工单' },
    children: [
      {
        path: '',
        name: 'Ticket',
        component: () => import('@/views/ticket/index.vue'),
        meta: { title: '我的工单' }
      }
    ]
  },
  // 用户直接打开 /index.html 时 history 路由无匹配会白屏
  {
    path: '/index.html',
    redirect: '/'
  },
  {
    path: '*',
    redirect: '/'
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach(async function(to, from, next) {
  const isPublic = isPublicRoute(to.path) || !!(to.meta && to.meta.public)
  const allowBanned = !!(to.meta && to.meta.allowBanned) || to.path === '/account-banned'

  if (to.path === '/account-banned') {
    if (!isLikelyLoggedIn()) {
      next({ path: '/login' })
      return
    }
    try {
      const user = await store.dispatch('loadCurrentUser', { force: true })
      if (!isBannedUser(user)) {
        next({ path: '/home' })
        return
      }
      next()
    } catch (e) {
      clearAuthLocal()
      next({ path: '/login' })
    }
    return
  }

  if (isPublic) {
    if (to.path === '/login' && isLikelyLoggedIn()) {
      try {
        const user = await store.dispatch('loadCurrentUser')
        if (isBannedUser(user)) {
          next({ path: '/account-banned' })
          return
        }
        next({ path: '/home' })
        return
      } catch (e) {
        clearAuthLocal()
        next()
        return
      }
    }
    next()
    return
  }

  if (!isLikelyLoggedIn()) {
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
    return
  }

  try {
    // 每次进入业务页强制拉取，便于封禁后及时落到说明页
    const user = await store.dispatch('loadCurrentUser', { force: true })
    if (isBannedUser(user) && !allowBanned) {
      next({ path: '/account-banned' })
      return
    }
    next()
  } catch (e) {
    clearAuthLocal()
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  }
})

router.afterEach(function(to) {
  syncToolPageProtection(isToolRoute(to))
})

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch((err) => {
    if (err.name !== 'NavigationDuplicated') {
      return Promise.reject(err)
    }
  })
}

export default router
